import { useQueryClient } from '@tanstack/vue-query'
import { FEATURE_FLAGS_QUERY_KEY } from '@/constants/QueryKeys'
import { QUERY_STATUSES } from '@/constants/QueryStatus'
import logger from '@/logger'

/**
 * Feature Flag Composable.
 *
 * This composable provides a way to check if a feature flag is enabled for the current account.
 * It ensures the feature flag data is loaded before checking, fetching it if necessary.
 *
 * @returns {Object} - Returns an object with methods to check if a feature flag is enabled.
 */
const useFeatureFlag = () => {
  const queryClient = useQueryClient()

  /**
   * Check if a feature flag is enabled for the current account.
   * Ensures data is loaded before checking.
   *
   * @param {String} flagName - The name of the feature flag to check.
   * @returns {Promise<Boolean>} - Promise resolving to true if the flag is enabled, false otherwise (including errors or loading failures).
   */
  const isEnabled = async (flagName) => {
    let queryState = queryClient.getQueryState([FEATURE_FLAGS_QUERY_KEY])

    // If query hasn't run or is loading, ensure it's fetched.
    if (!queryState || queryState.status === QUERY_STATUSES.PENDING) {
      logger.debug({ msg: `Feature flag query pending for check '${flagName}'. Fetching...` })
      try {
        await queryClient.fetchQuery({
          queryKey: [FEATURE_FLAGS_QUERY_KEY],
          staleTime: 0
        })
        queryState = queryClient.getQueryState([FEATURE_FLAGS_QUERY_KEY])
        logger.debug({ msg: `Feature flag query completed for check '${flagName}'.` })
      } catch (error) {
        logger.error({ msg: `Error fetching feature flags during check for '${flagName}':`, error })
        return false // Treat fetch error as flag disabled
      }
    }

    if (queryState.status === QUERY_STATUSES.ERROR) {
      logger.error({ msg: `Feature flag query failed. Treating '${flagName}' as disabled.` })
      return false
    }

    // Status should be 'success' here
    if (queryState.status === QUERY_STATUSES.SUCCESS) {
      const featureFlags = queryClient.getQueryData([FEATURE_FLAGS_QUERY_KEY])
      if (!featureFlags) {
        // Should not happen if status is success, but safeguard
        logger.warn({
          msg: 'Feature flags data missing despite success status. Treating as disabled.'
        })
        return false
      }
      return featureFlags.some((flag) => flag.name === flagName)
    } else {
      // Fallback for unexpected states
      logger.error({
        msg: `Unexpected query state '${queryState.status}' for flags. Treating '${flagName}' as disabled.`
      })
      return false
    }
  }

  return {
    isEnabled
  }
}

export default useFeatureFlag
